import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SectionCard from '../../components/ui/SectionCard';
import EmptyState from '../../components/ui/EmptyState';
import api from '../../api';
import { formatCurrency } from '../../utils/image';
import { dataPelada } from '../../utils/dataPelada';

const STATUS = { pendente: { label: 'A pagar', color: 'warning' }, pago: { label: 'Pago', color: 'success' }, cancelado: { label: 'Cancelado', color: 'default' } };

// Pagamentos a fazer para goleiros e árbitros (gerados no check-in). A baixa lança o débito no caixa.
const PagamentosEquipe = ({ onMessage, onAtualizarCaixa }) => {
  const [filtro, setFiltro] = useState('pendente');
  const [dados, setDados] = useState({ pagamentos: [], resumo: { pendente: 0, qtdPendente: 0 } });
  const [acao, setAcao] = useState(null); // { tipo: 'baixa'|'cancelar', pagamento }
  const [observacao, setObservacao] = useState('');
  const [enviando, setEnviando] = useState(false);

  const carregar = async () => {
    try {
      const response = await api.get('admin/pagamentos-equipe', { params: filtro === 'todos' ? {} : { status: filtro } });
      setDados(response.data);
    } catch (error) {
      onMessage && onMessage('Erro ao carregar pagamentos da equipe', 'error');
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro]);

  const copiarChave = async (chave) => {
    await navigator.clipboard.writeText(chave);
    onMessage && onMessage('Chave PIX copiada.');
  };

  const confirmar = async () => {
    setEnviando(true);
    try {
      await api.post(`admin/pagamentos-equipe/${acao.pagamento._id}/${acao.tipo === 'baixa' ? 'baixa' : 'cancelar'}`, { observacao: observacao || undefined });
      onMessage && onMessage(acao.tipo === 'baixa' ? `Pagamento de ${acao.pagamento.nome} baixado e lançado no caixa.` : 'Pagamento cancelado.');
      setAcao(null);
      setObservacao('');
      await carregar();
      onAtualizarCaixa && onAtualizarCaixa();
    } catch (error) {
      onMessage && onMessage(error.response?.data?.error || 'Não foi possível concluir.', 'error');
    }
    setEnviando(false);
  };

  return (
    <SectionCard
      title="Pagamentos da equipe (goleiros e árbitros)"
      subtitle={dados.resumo.qtdPendente > 0 ? `${dados.resumo.qtdPendente} a pagar · ${formatCurrency(dados.resumo.pendente)}` : 'Nenhum pagamento pendente'}
      icon={<PaymentsRoundedIcon color="primary" />}
      actions={
        <ToggleButtonGroup size="small" exclusive value={filtro} onChange={(e, v) => v && setFiltro(v)}>
          <ToggleButton value="pendente">A pagar</ToggleButton>
          <ToggleButton value="pago">Pagos</ToggleButton>
          <ToggleButton value="todos">Todos</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Criados no check-in do goleiro/árbitro. Só saem do caixa do clube quando você dá baixa após realizar o pagamento.
      </Typography>
      {dados.pagamentos.length === 0 ? (
        <EmptyState title={filtro === 'pendente' ? 'Nada a pagar' : 'Nenhum pagamento'} />
      ) : (
        <Stack spacing={1}>
          {dados.pagamentos.map((p) => (
            <Stack key={p._id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={600} noWrap>{p.nome}</Typography>
                  <Chip size="small" variant="outlined" label={p.tipo === 'goleiro' ? 'Goleiro' : 'Árbitro'} />
                  <Chip size="small" color={STATUS[p.status].color} label={STATUS[p.status].label} />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Pelada de {dataPelada(p.dataPelada, { day: '2-digit', month: '2-digit', year: 'numeric' }) || '-'} · check-in {p.criadoPor === 'próprio' ? 'pelo próprio' : `por ${p.criadoPor}`}
                  {p.pagoEm ? ` · pago em ${new Date(p.pagoEm).toLocaleDateString('pt-BR')} por ${p.pagoPor}` : ''}
                  {p.observacao ? ` · ${p.observacao}` : ''}
                </Typography>
                {p.status === 'pendente' && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2" color={p.chavePix ? 'text.primary' : 'warning.main'}>
                      {p.chavePix ? <>PIX: <strong>{p.chavePix}</strong></> : 'Sem chave PIX cadastrada'}
                    </Typography>
                    {p.chavePix && (
                      <Tooltip title="Copiar chave PIX">
                        <IconButton size="small" onClick={() => copiarChave(p.chavePix)}><ContentCopyRoundedIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                )}
              </Box>
              <Typography fontWeight={700} sx={{ minWidth: 90, textAlign: { sm: 'right' } }}>{formatCurrency(p.valor)}</Typography>
              {p.status === 'pendente' && (
                <Stack direction="row" spacing={1}>
                  <Button size="small" color="inherit" onClick={() => setAcao({ tipo: 'cancelar', pagamento: p })}>Cancelar</Button>
                  <Button size="small" variant="contained" onClick={() => setAcao({ tipo: 'baixa', pagamento: p })}>Dar baixa</Button>
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      <Dialog open={Boolean(acao)} onClose={() => setAcao(null)} fullWidth maxWidth="xs">
        <DialogTitle>{acao?.tipo === 'baixa' ? 'Confirmar pagamento' : 'Cancelar pagamento'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {acao?.tipo === 'baixa'
              ? `Confirma que ${acao?.pagamento.nome} recebeu ${formatCurrency(acao?.pagamento.valor)}${acao?.pagamento.chavePix ? ` na chave PIX ${acao.pagamento.chavePix}` : ''}? O valor será lançado como saída no caixa do clube.`
              : `O pagamento de ${acao?.pagamento.nome} será cancelado e não sairá do caixa.`}
          </DialogContentText>
          <TextField label="Observação (opcional)" value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder={acao?.tipo === 'baixa' ? 'Ex.: PIX enviado' : 'Motivo'} fullWidth />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAcao(null)} color="inherit">Voltar</Button>
          <Button onClick={confirmar} variant="contained" color={acao?.tipo === 'baixa' ? 'primary' : 'error'} disabled={enviando}>
            {acao?.tipo === 'baixa' ? 'Dar baixa' : 'Cancelar pagamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

export default PagamentosEquipe;
